import { useEffect, useRef, useState } from 'react';

interface UseHoverOptions {
    delayMs?: number;
}

export const useHover = <T extends HTMLElement>({ delayMs = 0 }: UseHoverOptions = {}) => {
    const ref = useRef<T | null>(null);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        const isCoarsePointer = matchMedia('(pointer:coarse)').matches;

        if (isCoarsePointer) {
            setIsHovered(true);
            return;
        }

        let hoverTimer: ReturnType<typeof setTimeout> | undefined;

        const handleMouseEnter = () => {
            hoverTimer = setTimeout(() => setIsHovered(true), delayMs);
        };

        const handleMouseLeave = () => {
            if (hoverTimer) {
                clearTimeout(hoverTimer);
            }
            setIsHovered(false);
        };

        const element = ref.current;
        if (!element) {
            return;
        }

        element.addEventListener('mouseenter', handleMouseEnter);
        element.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            element.removeEventListener('mouseenter', handleMouseEnter);
            element.removeEventListener('mouseleave', handleMouseLeave);
            if (hoverTimer) {
                clearTimeout(hoverTimer);
            }
        };
    }, [delayMs]);

    return { ref, isHovered };
};
